<template>
  <div class="content">
    <div class="users-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-users">

          <header class="glad-users__header">
            <p class="glad-eyebrow">{{ eyebrow }}</p>
            <h1 class="glad-users__title">Users</h1>
            <p class="glad-users__sub">{{ pageSub }}</p>
          </header>

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner"></span>
          </div>

          <div v-else-if="error" class="glad-error">
            {{ error }}
          </div>

          <template v-else>
            <!-- KPI cards -->
            <section class="glad-kpi-grid">
              <article
                v-for="kpi in kpiCards"
                :key="kpi.key"
                class="glad-kpi">
                <p class="glad-kpi__label">{{ kpi.label }}</p>
                <p class="glad-kpi__value">{{ kpi.value }}</p>
              </article>
            </section>

            <!-- Filter bar -->
            <section class="glad-filter-bar">
              <input
                v-model="search"
                type="text"
                class="glad-filter-input"
                placeholder="Search by name or email" />
              <select v-model="roleFilter" class="glad-filter-input glad-filter-select">
                <option value="">All roles</option>
                <option
                  v-for="r in meta.assignable_roles"
                  :key="r.value"
                  :value="r.value">
                  {{ r.label }}
                </option>
              </select>
            </section>

            <!-- Users table -->
            <div v-if="!filteredUsers.length" class="glad-empty">
              No users match your filters.
            </div>

            <div v-else class="glad-table-wrap">
              <table class="glad-table">
                <thead>
                  <tr>
                    <th scope="col">User</th>
                    <th scope="col">Role</th>
                    <th scope="col">RTC</th>
                    <th scope="col">Status</th>
                    <th scope="col">Last sign-in</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in filteredUsers" :key="user.id">
                    <!-- User -->
                    <td>
                      <span class="glad-user-name">
                        {{ user.name }}
                        <span v-if="isSelf(user)" class="glad-user-you">(you)</span>
                      </span>
                      <span class="glad-user-email">{{ user.email }}</span>
                    </td>

                    <!-- Role -->
                    <td>
                      <select
                        class="glad-row-select"
                        :value="user.glad_role"
                        :disabled="isSelf(user)"
                        @change="changeRole(user, $event)">
                        <option
                          v-for="r in meta.assignable_roles"
                          :key="r.value"
                          :value="r.value">
                          {{ r.label }}
                        </option>
                      </select>
                    </td>

                    <!-- RTC -->
                    <td>
                      <select
                        v-if="meta.can_assign_rtc"
                        class="glad-row-select"
                        :value="user.rtc_id"
                        @change="changeRtc(user, $event)">
                        <option
                          v-for="rtc in meta.assignable_rtcs"
                          :key="rtc.value"
                          :value="rtc.value">
                          {{ rtc.short || rtc.label }}
                        </option>
                      </select>
                      <span v-else class="glad-rtc-text">
                        {{ user.rtc_short_name || user.rtc_name || '—' }}
                      </span>
                    </td>

                    <!-- Status -->
                    <td>
                      <span
                        class="glad-status"
                        :class="user.is_active ? 'glad-status--active' : 'glad-status--inactive'">
                        {{ user.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>

                    <!-- Last sign-in -->
                    <td class="glad-signin">
                      {{ user.last_sign_in ? formatDate(user.last_sign_in) : 'Never' }}
                    </td>

                    <!-- Actions -->
                    <td>
                      <button
                        type="button"
                        class="glad-btn-sm"
                        :class="{ 'glad-btn-sm--danger': user.is_active }"
                        :disabled="isSelf(user)"
                        @click="toggleActive(user)">
                        {{ user.is_active ? 'Deactivate' : 'Activate' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GladUsers',
  data() {
    return {
      loading: true,
      error: '',
      users: [],
      meta: {
        assignable_roles: [],
        assignable_rtcs: [],
        can_assign_rtc: false,
      },
      scope: '',
      search: '',
      roleFilter: '',
      currentUserId: localStorage.getItem('hot-user-id'),
    };
  },
  computed: {
    eyebrow() {
      return this.scope === 'national' ? 'NTC Admin View' : 'RTC Admin View';
    },
    pageSub() {
      return this.scope === 'national'
        ? 'Manage educators, trainers, and admins across every RTC.'
        : 'Manage the members of your RTC.';
    },
    kpiCards() {
      const u = this.users || [];
      return [
        { key: 'total',     label: 'Total users', value: u.length },
        { key: 'active',    label: 'Active',      value: u.filter(x => x.is_active).length },
        { key: 'educators', label: 'Educators',   value: u.filter(x => x.glad_role === 'glad_educator').length },
        { key: 'trainers',  label: 'Trainers',    value: u.filter(x => x.glad_role === 'glad_trainer').length },
      ];
    },
    filteredUsers() {
      const q = (this.search || '').trim().toLowerCase();
      return this.users.filter(u => {
        if (this.roleFilter && u.glad_role !== this.roleFilter) return false;
        if (q) {
          const name = (u.name || '').toLowerCase();
          const email = (u.email || '').toLowerCase();
          if (name.indexOf(q) === -1 && email.indexOf(q) === -1) return false;
        }
        return true;
      });
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true;
      this.error = '';
      this.$http.get('glad/admin/users')
        .then(resp => {
          const body = resp.data || {};
          this.users = body.data || [];
          this.scope = body.scope || '';
          if (body.meta) {
            this.meta = {
              assignable_roles: body.meta.assignable_roles || [],
              assignable_rtcs: body.meta.assignable_rtcs || [],
              can_assign_rtc: !!body.meta.can_assign_rtc,
            };
          }
        })
        .catch(err => {
          if (err && err.response && err.response.status === 403) {
            this.error = 'User management is available to RTC and NTC Admins only.';
          } else {
            this.error = 'Could not load users.';
          }
        })
        .finally(() => { this.loading = false; });
    },

    isSelf(user) {
      return this.currentUserId != null
        && String(user.id) === String(this.currentUserId);
    },

    toast(kind, msg) {
      if (this.$toast && this.$toast[kind]) {
        this.$toast[kind](msg, { timeout: 4000 });
      }
    },

    serverMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (data) {
        if (typeof data.message === 'string' && data.message) return data.message;
        if (typeof data.error === 'string' && data.error) return data.error;
      }
      return fallback;
    },

    replaceRow(user, updated) {
      const idx = this.users.indexOf(user);
      if (idx !== -1) {
        this.$set(this.users, idx, updated);
      }
    },

    changeRole(user, event) {
      const previous = user.glad_role;
      const newValue = event.target.value;
      if (newValue === previous) return;
      this.$http.put('glad/admin/users/' + user.id, { glad_role: newValue })
        .then(resp => {
          this.replaceRow(user, resp.data.data);
          this.toast('success', 'Role updated.');
        })
        .catch(err => {
          user.glad_role = previous;
          event.target.value = previous;
          this.$forceUpdate();
          this.toast('error', this.serverMessage(err, 'Could not update role.'));
        });
    },

    changeRtc(user, event) {
      const previous = user.rtc_id;
      const newValue = event.target.value;
      // <select> values are strings; coerce to number to match rtc ids.
      const numeric = newValue === '' ? null : Number(newValue);
      if (numeric === previous) return;
      this.$http.put('glad/admin/users/' + user.id, { rtc_id: numeric })
        .then(resp => {
          this.replaceRow(user, resp.data.data);
          this.toast('success', 'RTC updated.');
        })
        .catch(err => {
          user.rtc_id = previous;
          event.target.value = previous;
          this.$forceUpdate();
          this.toast('error', this.serverMessage(err, 'Could not reassign RTC.'));
        });
    },

    toggleActive(user) {
      const targetStatus = user.is_active ? 0 : 1;
      this.$http.put('glad/admin/users/' + user.id, { status: targetStatus })
        .then(resp => {
          this.replaceRow(user, resp.data.data);
          this.toast('success', targetStatus ? 'User activated.' : 'User deactivated.');
        })
        .catch(err => {
          this.toast('error', this.serverMessage(err, 'Could not update user status.'));
        });
    },

    formatDate(d) {
      if (!d) return 'Never';
      // Laravel returns "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS" without TZ.
      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(d)
        ? d
        : d.replace(' ', 'T') + 'Z';
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) return d;
      const now = new Date();
      const diffMs = now.getTime() - parsed.getTime();
      const diffHr = Math.round(diffMs / 3600000);
      if (diffHr < 1)  return 'just now';
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.users-wrap { width: 100%; }
.users-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-users {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Header ------------------------------------------------------------------- */
.glad-users__header { margin-bottom: 1.75rem; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-users__title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-users__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* Loading + error ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-error {
  padding: 2.5rem 1rem;
  text-align: center;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}

/* KPI grid ----------------------------------------------------------------- */
.glad-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.75rem;
}
.glad-kpi {
  padding: 1.1rem 1.15rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
}
.glad-kpi__label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--glad-text-soft);
  margin: 0 0 0.4rem;
}
.glad-kpi__value {
  font-family: var(--glad-font-serif);
  font-size: 1.85rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
  line-height: 1;
}

/* Filter bar --------------------------------------------------------------- */
.glad-filter-bar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
  flex-wrap: wrap;
}
.glad-filter-input {
  font-family: var(--glad-font-sans);
  font-size: 0.88rem;
  color: var(--glad-ink);
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm);
  padding: 0.5rem 0.7rem;
  outline: none;

  &:focus {
    border-color: var(--glad-line-strong);
  }
}
.glad-filter-input[type="text"] {
  flex: 1;
  min-width: 220px;
}
.glad-filter-select {
  min-width: 180px;
}

/* Empty state -------------------------------------------------------------- */
.glad-empty {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  text-align: center;
  padding: 1.5rem 0;
}

/* Table -------------------------------------------------------------------- */
.glad-table-wrap {
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  overflow: hidden;
}
.glad-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid var(--glad-line);
    vertical-align: middle;
  }
  tr:last-child td { border-bottom: 0; }

  thead th {
    background: var(--glad-paper);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--glad-text-soft);
  }
}

/* User cell ---------------------------------------------------------------- */
.glad-user-name {
  display: block;
  font-weight: 600;
  color: var(--glad-ink);
}
.glad-user-you {
  font-weight: 500;
  font-size: 0.74rem;
  color: var(--glad-text-muted);
  margin-left: 0.25rem;
}
.glad-user-email {
  display: block;
  font-size: 0.78rem;
  color: var(--glad-text-muted);
  margin-top: 0.1rem;
}

/* Row selects -------------------------------------------------------------- */
.glad-row-select {
  font-family: var(--glad-font-sans);
  font-size: 0.84rem;
  color: var(--glad-ink);
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm);
  padding: 0.4rem 0.55rem;
  outline: none;
  max-width: 220px;

  &:focus { border-color: var(--glad-line-strong); }
  &:disabled {
    background: var(--glad-paper-2);
    color: var(--glad-text-muted);
    cursor: not-allowed;
  }
}
.glad-rtc-text {
  color: var(--glad-text-soft);
}

/* Status pill -------------------------------------------------------------- */
.glad-status {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;

  &--active {
    background: rgba(180, 83, 9, 0.12);
    color: var(--glad-accent);
  }
  &--inactive {
    background: var(--glad-paper-2);
    color: var(--glad-text-muted);
  }
}

.glad-signin {
  color: var(--glad-text-soft);
  font-size: 0.82rem;
  white-space: nowrap;
}

/* Action button ------------------------------------------------------------ */
.glad-btn-sm {
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--glad-ink);
  background: var(--glad-surface);
  border: 1px solid var(--glad-line-strong);
  border-radius: var(--glad-radius-sm);
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: var(--glad-paper-2); }

  &:disabled {
    color: var(--glad-text-muted);
    border-color: var(--glad-line);
    background: var(--glad-paper-2);
    cursor: not-allowed;
  }

  &--danger {
    color: #b3261e;
    border-color: #b3261e;

    &:hover { background: rgba(179, 38, 30, 0.06); }

    &:disabled {
      color: var(--glad-text-muted);
      border-color: var(--glad-line);
      background: var(--glad-paper-2);
    }
  }
}
</style>
